import * as Sentry from "@sentry/react";

/**
 * Inicializa o Sentry do navegador.
 *
 * Precisa ser importado antes do resto da aplicacao para capturar falhas que
 * acontecem durante o boot. Sem `VITE_SENTRY_DSN` o SDK nao inicializa e tudo
 * vira no-op, o que mantem o desenvolvimento local silencioso por padrao.
 *
 * Le `import.meta.env` diretamente, e nao o runtime-config, porque a validacao
 * do runtime-config pode ela mesma lancar; queremos o Sentry ativo antes disso.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

const REDACTED = "[REDACTED]";

const SENSITIVE_KEY_PATTERNS = [
  "authorization",
  "cookie",
  "token",
  "secret",
  "password",
  "senha",
  "cpf",
  "cnpj",
  "email",
  "apikey",
  "api_key",
  "access_token",
  "refresh_token",
];

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function scrub(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0,
): unknown {
  if (depth > 6 || value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (Array.isArray(value))
    return value.map((item) => scrub(item, seen, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isSensitiveKey(key) ? REDACTED : scrub(item, seen, depth + 1);
  }
  return out;
}

/**
 * O token do Supabase viaja em query string em alguns fluxos de Auth, como
 * recuperacao de senha. Remover a query inteira e mais seguro do que tentar
 * listar todos os parametros sensiveis.
 */
function stripQuery(url: string | undefined): string | undefined {
  if (!url) return url;
  const index = url.indexOf("?");
  return index === -1 ? url : `${url.slice(0, index)}?[stripped]`;
}

if (dsn) {
  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ||
      import.meta.env.MODE,
    sendDefaultPii: false,
    // Amostra baixa: o valor aqui e o erro, nao o tracing.
    tracesSampleRate: 0.1,
    beforeSend(event) {
      if (event.request) {
        event.request.url = stripQuery(event.request.url);
        event.request = scrub(event.request) as typeof event.request;
      }
      if (event.user) event.user = { id: event.user.id };
      if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
      if (event.contexts)
        event.contexts = scrub(event.contexts) as typeof event.contexts;
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data)
        breadcrumb.data = scrub(breadcrumb.data) as typeof breadcrumb.data;
      // Breadcrumbs de navegacao e fetch carregam URLs com query string.
      if (typeof breadcrumb.data?.url === "string") {
        breadcrumb.data.url = stripQuery(breadcrumb.data.url);
      }
      return breadcrumb;
    },
  });
}

/** Reporta um erro tratado, preservando o contexto de onde ele aconteceu. */
export function reportError(
  error: unknown,
  context: Record<string, string> = {},
): void {
  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context))
      scope.setTag(key, value);
    Sentry.captureException(error);
  });
}
